/**
 * BufferPicker - Dropdown for switching between session buffers
 *
 * Shows in workspace header. Displays current buffer name as a chip,
 * expands to list all session buffers with metadata and actions.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useBufferSync } from '../../contexts/BufferSyncContext';
import { useApi, type BufferType } from '../../contexts/ApiContext';
import { usePlatform } from '../../contexts/PlatformContext';
import { createLogger } from '../../utils/debug';

const log = createLogger('BufferPicker');

const TYPE_LABELS: Record<BufferType, string> = {
  working: 'W',
  voice: 'V',
  reference: 'R',
  arc: 'A',
};

interface UserBuffer {
  name: string;
  bufferType: BufferType;
  contentLength: number;
  scope: 'user';
}

export function BufferPicker() {
  const {
    activeBufferName,
    buffers,
    isDirty,
    syncError,
    switchBuffer,
    createBuffer,
    deleteBuffer,
    pinBuffer,
    refreshBuffers,
    sessionId,
  } = useBufferSync();
  const api = useApi();
  const { isElectron } = usePlatform();

  const [isOpen, setIsOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [userBuffers, setUserBuffers] = useState<UserBuffer[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setIsCreating(false);
        setConfirmDelete(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  // Focus input when creating
  useEffect(() => {
    if (isCreating && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isCreating]);

  // Refresh buffers when opening (including user buffers)
  const handleToggle = useCallback(() => {
    if (!isOpen) {
      refreshBuffers();
      // On Electron, buffers are managed via IPC — skip cloud API call
      if (!isElectron) {
        api.userBuffers.listAll(sessionId ?? undefined).then(({ buffers: all }) => {
          setUserBuffers(all.filter((b: any) => b.scope === 'user') as UserBuffer[]);
        }).catch(() => {});
      }
    }
    setIsOpen((v) => !v);
    setIsCreating(false);
    setConfirmDelete(null);
  }, [isOpen, refreshBuffers, api, sessionId, isElectron]);

  const handleSwitch = useCallback(async (nameOrId: string) => {
    if (nameOrId === activeBufferName) return;
    try {
      await switchBuffer(nameOrId);
      setIsOpen(false);
    } catch (err) {
      log.error('Failed to switch buffer:', err);
    }
  }, [activeBufferName, switchBuffer]);

  const handleCreate = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    await createBuffer(name);
    await switchBuffer(name);
    setNewName('');
    setIsCreating(false);
    setIsOpen(false);
  }, [newName, createBuffer, switchBuffer]);

  const handleDelete = useCallback(async (name: string) => {
    if (confirmDelete !== name) {
      setConfirmDelete(name);
      return;
    }
    const buf = buffers.find((b) => b.name === name);
    const bufType = (buf as { bufferType?: string } | undefined)?.bufferType;
    const force = bufType === 'voice' || bufType === 'arc';
    await deleteBuffer(name, force);
    setConfirmDelete(null);
  }, [confirmDelete, buffers, deleteBuffer]);

  const handleRename = useCallback(async () => {
    if (!renamingId || !renameValue.trim() || !window.electronBuffer?.rename) return;
    try {
      await window.electronBuffer.rename(renamingId, renameValue.trim());
      refreshBuffers();
    } catch (err) {
      log.error('Rename failed:', err);
    }
    setRenamingId(null);
    setRenameValue('');
  }, [renamingId, renameValue, refreshBuffers]);

  const handlePin = useCallback(async (name: string, currentlyPinned: boolean) => {
    await pinBuffer(name, !currentlyPinned);
  }, [pinBuffer]);

  const activeBuf = buffers.find((b) => b.name === activeBufferName);
  const branch = activeBuf?.currentBranch ?? 'main';

  return (
    <div className="buffer-picker" ref={dropdownRef}>
      <button
        className="buffer-picker__trigger"
        onClick={handleToggle}
        title="Switch buffer"
      >
        <span className="buffer-picker__name">{activeBufferName}</span>
        {branch !== 'main' && (
          <span className="buffer-picker__branch">{branch}</span>
        )}
        {isDirty && <span className="buffer-picker__dirty" />}
        <span className="buffer-picker__caret">{isOpen ? '\u25B4' : '\u25BE'}</span>
      </button>

      {isOpen && (
        <div className="buffer-picker__dropdown">
          {syncError && (
            <div className="buffer-picker__sync-error" role="alert">
              {syncError}
            </div>
          )}
          <div className="buffer-picker__list">
            {buffers.map((buf) => {
              const isActive = buf.name === activeBufferName;
              const isPinned = (buf as unknown as { pinned?: boolean }).pinned === true;
              const bufType = (buf as unknown as { bufferType?: BufferType }).bufferType;
              const switchKey = buf.id ?? buf.name;
              const deleteKey = buf.id ?? buf.name;

              return (
                <div
                  key={switchKey}
                  className={`buffer-picker__item ${isActive ? 'buffer-picker__item--active' : ''}`}
                >
                  {renamingId === switchKey ? (
                    <input
                      className="buffer-picker__rename-input"
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleRename();
                        if (e.key === 'Escape') { setRenamingId(null); setRenameValue(''); }
                      }}
                      onBlur={() => handleRename()}
                      autoFocus
                    />
                  ) : (
                    <button
                      className="buffer-picker__item-btn"
                      onClick={() => handleSwitch(switchKey)}
                    >
                      {bufType && (
                        <span className={`buffer-picker__type buffer-picker__type--${bufType}`}>
                          {TYPE_LABELS[bufType] ?? 'W'}
                        </span>
                      )}
                      <span className="buffer-picker__item-name">{buf.name}</span>
                      <span className="buffer-picker__item-meta">
                        {isElectron
                          ? `${buf.contentLength.toLocaleString()} words \u00B7 ${buf.version}`
                          : `${buf.contentLength} items`}
                      </span>
                    </button>
                  )}
                  <div className="buffer-picker__item-actions">
                    {isElectron && window.electronBuffer?.rename && renamingId !== switchKey && (
                      <button
                        className="buffer-picker__rename"
                        onClick={() => { setRenamingId(switchKey); setRenameValue(buf.name); }}
                        title="Rename buffer"
                      >
                        &#x270E;
                      </button>
                    )}
                    {!isElectron && (
                      <button
                        className={`buffer-picker__pin ${isPinned ? 'buffer-picker__pin--active' : ''}`}
                        onClick={() => handlePin(buf.name, isPinned)}
                        title={isPinned ? 'Unpin' : 'Pin'}
                      >
                        {isPinned ? '\u2605' : '\u2606'}
                      </button>
                    )}
                    {buf.name !== 'workspace' && (
                      <button
                        className={`buffer-picker__delete ${confirmDelete === deleteKey ? 'buffer-picker__delete--confirm' : ''}`}
                        onClick={() => handleDelete(deleteKey)}
                        title={confirmDelete === deleteKey ? 'Click again to confirm' : 'Delete buffer'}
                      >
                        {confirmDelete === deleteKey ? '!!' : '\u00D7'}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* User Buffers Section */}
          {userBuffers.length > 0 && (
            <>
              <div className="buffer-picker__section-label">User Buffers</div>
              {userBuffers.map((buf) => (
                <div
                  key={`user-${buf.name}`}
                  className="buffer-picker__item"
                >
                  <button
                    className="buffer-picker__item-btn"
                    onClick={() => handleSwitch(buf.name)}
                  >
                    <span className="buffer-picker__scope-badge">U</span>
                    {buf.bufferType && (
                      <span className={`buffer-picker__type buffer-picker__type--${buf.bufferType}`}>
                        {TYPE_LABELS[buf.bufferType] ?? 'W'}
                      </span>
                    )}
                    <span className="buffer-picker__item-name">{buf.name}</span>
                    <span className="buffer-picker__item-meta">
                      {buf.contentLength} items
                    </span>
                  </button>
                </div>
              ))}
            </>
          )}

          {isCreating ? (
            <form className="buffer-picker__create-form" onSubmit={handleCreate}>
              <input
                ref={inputRef}
                className="buffer-picker__create-input"
                type="text"
                placeholder="Buffer name..."
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
              <button className="buffer-picker__create-submit" type="submit">+</button>
            </form>
          ) : (
            <button
              className="buffer-picker__new-btn"
              onClick={() => setIsCreating(true)}
            >
              + New Buffer
            </button>
          )}
        </div>
      )}
    </div>
  );
}
